import { ChangeDetectionStrategy, Component, Input, SimpleChanges } from "@angular/core";
import { BehaviorSubject, filter, Observable, ReplaySubject, switchMap, takeUntil } from "rxjs";
import { SpxLaunch } from "../../data/spx-launch";
import { MissionService } from "../../services/mission-service";

type LaunchTimeStyle = "table" | "page" | "widget";
type JustifyContent = "left" | "center" | "right";

@Component({
  selector: "launch-time",
  templateUrl: "./launch-time.component.html",
  styleUrls: ["./launch-time.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LaunchTimeComponent {
  @Input() public isMobile: boolean = false;
  @Input() public type: LaunchTimeStyle = "table";
  @Input() public justify: JustifyContent = "right";
  @Input() public justifyMobile: JustifyContent = "left";
  @Input() public alwaysShowCountdown: boolean = false;
  @Input() public missionId: string;

  private readonly _destroy$ = new ReplaySubject<void>(1);
  private _launch$: BehaviorSubject<SpxLaunch> = new BehaviorSubject(null);
  readonly launch$: Observable<SpxLaunch> = this._launch$.asObservable();
  private _missionId$: BehaviorSubject<string> = new BehaviorSubject(null);
  readonly missionId$: Observable<string> = this._missionId$.asObservable();

  private dateOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };

  private dateOptionsUtc: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC"
  };

  private timeOptions: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };

  // This needs to be any as the 'shortGeneric' time zone name does not exist until es2022
  private timeOptionsWithTimeZone: any = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZoneName: "shortGeneric",
  };

  constructor(private missionService: MissionService) { }

  ngOnInit() {
    this.missionId$.pipe(
      filter(id => !!id),
      switchMap(id => this.missionService.getTileForMission(this.missionId)),
      takeUntil(this._destroy$)
    ).subscribe(launch => this._launch$.next(launch));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.missionId && changes.missionId.currentValue) {
      this._missionId$.next(changes.missionId.currentValue);
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  shouldShowCountdown(launch: SpxLaunch): boolean {
    const hasPastDate: boolean = !!launch.launchDate && !!launch.launchTime;
    const hasUpcomingDate: boolean = launch.timings?.PrimaryLaunchDate?.Seconds != null;
    const hasOverrideDate: boolean = (!!launch.override?.date && !!launch.override?.time) ||
      (!!launch.override?.windowOpenDate && !!launch.override?.windowOpenTime);
    const canShowCountdownFromNonPadClock = this.alwaysShowCountdown && launch.missionStatus !== "final";
    return (canShowCountdownFromNonPadClock && (hasOverrideDate || hasUpcomingDate || hasPastDate) && !this.shouldShowOverrideText(launch))
      || !!launch.timings?.TZeroLaunchDate;
  }

  shouldShowOverrideText(launch: SpxLaunch): boolean {
    return !!launch.override?.text && !launch.timings?.TZeroLaunchDate;
  }

  shouldShowDate(launch: SpxLaunch): boolean {
    const hasOverrideDate: boolean = !!launch.override?.date &&
      new Date(`${launch.override.date}`).getTime() > Date.now();
    const hasPastDate: boolean = !!launch.launchDate && !!launch.launchTime;
    const hasUpcomingDate: boolean = launch.timings?.PrimaryLaunchDate?.Seconds != null;
    return (hasOverrideDate || hasUpcomingDate || hasPastDate) &&
      !this.shouldShowWindow(launch) &&
      !this.shouldShowOverrideText(launch) &&
      !this.shouldShowCountdown(launch);
  }

  shouldShowWindow(launch: SpxLaunch): boolean {
    const hasOverrideDate: boolean = !!launch.override?.date &&
      new Date(`${launch.override.date}`).getTime() > Date.now();
    const hasOverrideWindow: boolean =
      !!launch.override?.windowOpenDate && !!launch.override?.windowOpenTime &&
      !!launch.override?.windowCloseDate && !!launch.override?.windowCloseTime &&
      new Date(`${launch.override.windowCloseDate} ${launch.override.windowCloseTime}`).getTime() > Date.now();
    const hasWindow: boolean =
      launch.timings?.PrimaryLaunchWindow?.Open?.Seconds != null &&
      launch.timings?.PrimaryLaunchWindow?.Close?.Seconds != null;

    return (hasOverrideWindow || hasWindow) &&
      !this.shouldShowOverrideText(launch) &&
      !this.shouldShowCountdown(launch) &&
      !hasOverrideDate;
  }

  shouldShowTbd(launch: SpxLaunch): boolean {
    return !this.shouldShowOverrideText(launch) &&
      !this.shouldShowCountdown(launch) &&
      !this.shouldShowDate(launch) &&
      !this.shouldShowWindow(launch);
  }

  dateText(launch: SpxLaunch): string {
    let date: Date;
    let shouldShowTime: boolean = false;

    if (launch.override?.date && launch.override?.time) {
      date = this.missionService.getLaunchTimeWithTimezone(
        `${launch.override.date}T${launch.override.time}`, launch.launchSite);
      shouldShowTime = true;
    } else if (launch.override?.date) {
      date = new Date(launch.override.date);
    } else if (launch.timings?.PrimaryLaunchDate?.Seconds != null) {
      date = new Date(launch.timings!.PrimaryLaunchDate!.Seconds * 1000);
    } else {
      date = new Date(launch.launchDate);
    }

    // Use date options if showing time so users see date and time in their local time zone.
    const dateTimeString: string = `${date.toLocaleDateString("en-US", this.dateOptions)} ${date.toLocaleTimeString("en-US", this.timeOptionsWithTimeZone)}`;

    // Otherwise use utc date options to ensure the date shows the same for everyone worldwide.
    const dateString: string = date.toLocaleDateString("en-US", this.dateOptionsUtc);

    if (shouldShowTime) {
      return dateTimeString;
    }

    if (launch.endDate) {
      return `${dateString} - ${(new Date(launch.endDate)).toLocaleDateString("en-US", this.dateOptionsUtc)}`;
    }

    return dateString;
  }

  windowText(launch: SpxLaunch): string {
    if (this.isMobile) {
      let date: Date;

      if (launch.override?.windowOpenDate) {
        date = this.missionService.getLaunchTimeWithTimezone(
          `${launch.override.windowOpenDate}T${launch.override.windowOpenTime}`, launch.launchSite);
      } else {
        date = new Date(launch.timings!.PrimaryLaunchDate!.Seconds * 1000);
      }

      return date.toLocaleDateString("en-US", this.dateOptions);
    }

    let openDate: Date;
    let closeDate: Date;

    if (launch.override?.windowOpenDate) {
      openDate = this.missionService.getLaunchTimeWithTimezone(
        `${launch.override.windowOpenDate}T${launch.override.windowOpenTime}`, launch.launchSite);
      closeDate = this.missionService.getLaunchTimeWithTimezone(
        `${launch.override.windowCloseDate}T${launch.override.windowCloseTime}`, launch.launchSite);
    } else {
      openDate = new Date(launch.timings!.PrimaryLaunchWindow!.Open.Seconds * 1000);
      closeDate = new Date(launch.timings!.PrimaryLaunchWindow!.Close.Seconds * 1000);
    }

    const openDateString: string = openDate.toLocaleDateString("en-US", this.dateOptions);
    const closeDateString: string = closeDate.toLocaleDateString("en-US", this.dateOptions);
    const sameDay: boolean = openDateString == closeDateString;

    return sameDay
      ? `${openDateString} ${openDate.toLocaleTimeString("en-US", this.timeOptions)} - ${closeDate.toLocaleTimeString(
        "en-US",
        this.timeOptionsWithTimeZone
      )}`
      : `${openDateString} ${openDate.toLocaleTimeString(
        "en-US",
        this.timeOptionsWithTimeZone
      )} - ${closeDateString} ${closeDate.toLocaleTimeString("en-US", this.timeOptionsWithTimeZone)}`;
  }
}
